/**
 * Web push (VAPID) registration.
 *
 * Registers the browser's push subscription against the backend
 * `save-push-subscription` Edge Function and mirrors deletions. Deliberately
 * Firebase-free — uses the standards Push API + a Supabase-stored subscription.
 *
 * The interface (registerPushToken / unregisterPushToken) is unchanged from the
 * earlier no-op stub, so callers (notification settings UI, onboarding,
 * PushDeviceCheck) need no changes.
 */

import { getSupabaseAuthClient } from '../supabase'

const VAPID_PUBLIC_KEY = process.env.GATSBY_VAPID_PUBLIC_KEY

const SERVICE_WORKER_URL = '/push-sw.js'

const pushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  typeof Notification !== 'undefined'

// base64url → Uint8Array, required for applicationServerKey.
const urlBase64ToUint8Array = (base64: string) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized)
  const output = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

// POST to the save-push-subscription Edge Function. supabase-js attaches the
// signed-in user's JWT (Authorization) and apikey automatically; the function
// derives account_id from the token — never send an account id.
const callSaveSubscription = async (body: Record<string, unknown>) => {
  const supabase = getSupabaseAuthClient()
  if (!supabase) {
    console.warn('[push] Supabase auth client unavailable; skipping subscription sync')
    return
  }

  const { error } = await supabase.functions.invoke('save-push-subscription', { body })
  if (error) {
    console.error('[push] save-push-subscription failed', error)
    throw error
  }
}

export const registerPushToken = async (userId: string): Promise<string | null> => {
  void userId // account is derived server-side from the auth token
  if (!pushSupported()) {
    return null
  }
  if (!VAPID_PUBLIC_KEY) {
    console.warn('[push] no VAPID public key (GATSBY_VAPID_PUBLIC_KEY)')
    return null
  }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    return null
  }

  // Callers don't handle rejections (settings toggle, onboarding, PushDeviceCheck), so
  // failures are logged and reported as null rather than thrown.
  try {
    const registration = await navigator.serviceWorker.register(SERVICE_WORKER_URL)
    await navigator.serviceWorker.ready

    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)

    // Reuse an existing subscription if it was made with the current VAPID key. One
    // made with an old key would be rejected by the push service, so replace it.
    let existing = await registration.pushManager.getSubscription()
    if (existing && !sameKey(existing.options.applicationServerKey, applicationServerKey)) {
      await existing.unsubscribe()
      existing = null
    }
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true, // mandatory in Chrome
        applicationServerKey
      }))

    // subscription.toJSON() = { endpoint, keys: { p256dh, auth } } — the backend shape.
    await callSaveSubscription({ kind: 'save', subscription: subscription.toJSON() })
    return subscription.endpoint
  } catch (err) {
    console.error('[push] registration failed', err)
    return null
  }
}

const sameKey = (a: ArrayBuffer | null, b: Uint8Array) => {
  if (!a || a.byteLength !== b.byteLength) return false
  const view = new Uint8Array(a)
  return view.every((byte, i) => byte === b[i])
}

// Registrations running our push worker. Matched on the worker's script URL rather
// than via getRegistration(), which takes a *scope* URL and would return whichever
// worker controls "/" — we must never unregister a worker that isn't ours.
const getPushRegistrations = async (): Promise<ServiceWorkerRegistration[]> => {
  const registrations = await navigator.serviceWorker.getRegistrations()
  return registrations.filter(registration => {
    const worker = registration.active || registration.waiting || registration.installing
    return worker ? new URL(worker.scriptURL).pathname === SERVICE_WORKER_URL : false
  })
}

/**
 * This device's push readiness, for when the account has push turned on:
 *   unsupported — no Push API / service workers, or no VAPID key in this build
 *   denied      — the user blocked notifications for this site
 *   prompt      — permission hasn't been asked yet (needs a user gesture to ask)
 *   missing     — permission granted, but no subscription made with the current
 *                 VAPID key (new device, cleared site data, expired or pruned)
 *   subscribed  — ready to receive pushes
 */
export type DevicePushStatus = 'unsupported' | 'denied' | 'prompt' | 'missing' | 'subscribed'

export const getDevicePushStatus = async (): Promise<DevicePushStatus> => {
  if (!pushSupported() || !VAPID_PUBLIC_KEY) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  if (Notification.permission === 'default') return 'prompt'

  try {
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    for (const registration of await getPushRegistrations()) {
      const subscription = await registration.pushManager.getSubscription()
      if (subscription && sameKey(subscription.options.applicationServerKey, applicationServerKey)) {
        return 'subscribed'
      }
    }
  } catch (err) {
    console.error('[push] device status check failed', err)
  }
  return 'missing'
}

/**
 * Turns push off for this browser: removes the backend subscription, unsubscribes
 * from the push service, and unregisters the push service worker entirely.
 */
export const unregisterPushToken = async (userId?: string): Promise<void> => {
  void userId // account is derived server-side from the auth token
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }

  try {
    const registrations = await getPushRegistrations()
    await Promise.all(
      registrations.map(async registration => {
        const subscription = await registration.pushManager.getSubscription()
        if (subscription) {
          // Unsubscribe locally even if the backend delete fails — the drainer prunes
          // the row when the push service reports the endpoint gone.
          await callSaveSubscription({ kind: 'delete', endpoint: subscription.endpoint }).catch(
            () => undefined
          )
          await subscription.unsubscribe().catch(err => {
            console.error('[push] unsubscribe failed', err)
          })
        }
        // Remove the worker even when there was no subscription (e.g. permission was
        // granted but subscribe failed), so toggling off leaves nothing behind.
        const removed = await registration.unregister()
        if (!removed) console.warn('[push] service worker unregister returned false')
      })
    )
  } catch (err) {
    console.error('[push] unregister failed', err)
  }
}

/**
 * No-op placeholder retained for callers that wire foreground-message
 * suppression. Native web push shows notifications via the service worker, so
 * there is nothing to suppress here.
 */
export const initForegroundMessageSuppression = (): void => {}
